/**
 * @file Unified app menu button combining user, sync, PWA, and settings sections.
 */

import UserData from '../user/components/UserData.js';
import UserAuthButtons from '../user/components/UserAuthButtons.js';
import UserPanel from '../user/components/UserPanel.js';
import ConfigEditor from '../configs/components/ConfigEditor.js';
import DataPortabilityPanel from '../data-portability/components/DataPortabilityPanel.js';
import { useUser } from '../user/composables/user.js';
import { useDb } from '../core/db.js';
import { useNetworkStatus } from '../core/networkStatus.js';
import { usePwa } from '../pwa/composables/pwa.js';

const PWA_FALLBACK = {
  currentVersion: null,
  currentDeployedAt: null,
  availableVersion: null,
  availableDeployedAt: null,
  swEnabled: false,
  swActive: false,
  isInstalled: false,
  isInstallable: false,
  updateAvailable: false,
  installingUpdate: false,
  needsReloadForInstall: false,
  isOffline: false,
  install: async () => 'unavailable',
  applyUpdate: async () => {},
};

function isIosDevice() {
  if (typeof navigator === 'undefined') return false;
  if (/iPad|iPhone|iPod/.test(navigator.userAgent)) return true;
  return (
    navigator.platform === 'MacIntel' &&
    typeof navigator.maxTouchPoints === 'number' &&
    navigator.maxTouchPoints > 1
  );
}

export default {
  name: 'AppButton',

  components: {
    UserData,
    UserAuthButtons,
    UserPanel,
    ConfigEditor,
    DataPortabilityPanel,
  },

  props: {
    /** Bootstrap icon class for the trigger, e.g. 'bi bi-grid-3x3-gap-fill' */
    appIcon: { type: String, default: 'bi bi-grid-3x3-gap-fill' },
    /** Optional label shown next to the icon */
    appName: { type: String, default: null },
    showUser: { type: Boolean, default: true },
    showSync: { type: Boolean, default: true },
    showPwa: { type: Boolean, default: true },
    showDataButton: { type: Boolean, default: true },
    showHardResetButton: { type: Boolean, default: true },
  },

  data() {
    return { activeConfig: '_config' };
  },

  computed: {
    configs() {
      return this.db.configs.filter(
        c => !c.schema.editableBy || this.user.hasAnyRole(c.schema.editableBy)
      );
    },

    collections() {
      return Object.fromEntries(this.db.collections.map(c => [c.name, c]));
    },

    hasSyncErrors() {
      return this.db.collections.some(c => c.syncErrors.length > 0);
    },

    hasBanners() {
      return (
        (this.showSync && this.hasSyncErrors) ||
        (this.showPwa && this.pwa.updateAvailable)
      );
    },

    attentionLevel() {
      if (this.showSync && this.hasSyncErrors) return 'error';
      if (this.showPwa && this.pwa.updateAvailable) return 'info';
      return null;
    },

    ios() {
      return isIosDevice();
    },

    primaryAction() {
      if (this.pwa.updateAvailable) return 'update';
      if (!this.pwa.swEnabled) return 'none';
      if (this.pwa.isInstallable) return 'install';
      if (this.pwa.needsReloadForInstall && !this.ios && !this.pwa.isInstalled)
        return 'reload';
      if (this.ios && !this.pwa.isInstalled) return 'ios-help';
      if (this.pwa.swActive && this.pwa.isOffline) return 'offline';
      // 'online' only when not installed — installed users don't need the notice
      if (this.pwa.swActive && !this.pwa.isInstalled) return 'online';
      return 'none';
    },

    // True when the browser cannot trigger an install prompt and the app is not installed.
    // Shown as a hint to use Chrome or Edge instead. Only shown when no more
    // specific action (update, install, reload, offline) is already displayed.
    showInstallHint() {
      return (
        this.pwa.swEnabled &&
        (this.primaryAction === 'none' || this.primaryAction === 'online') &&
        !this.ios &&
        !this.pwa.isInstalled &&
        !this.pwa.isInstallable &&
        !this.pwa.needsReloadForInstall
      );
    },

    actionMessage() {
      switch (this.primaryAction) {
        case 'install':
          return 'Install this app to use it offline.';
        case 'reload':
          return 'Reload the page to enable installing this app.';
        case 'ios-help':
          return 'Install this app on your home screen:';
        case 'offline':
          return 'You are offline. The app is running from its cached version.';
        default:
          return '';
      }
    },

    formattedCurrentVersion() {
      return this._formatVersionLine(
        this.pwa.currentVersion,
        this.pwa.currentDeployedAt
      );
    },

    formattedAvailableVersion() {
      return this._formatVersionLine(
        this.pwa.availableVersion,
        this.pwa.availableDeployedAt
      );
    },
  },

  watch: {
    configs(newConfigs) {
      if (!newConfigs.some(c => c.name === this.activeConfig)) {
        this.activeConfig = newConfigs[0]?.name ?? '';
      }
    },
  },

  methods: {
    _formatVersionLine(version, deployedAt) {
      const v = version || 'unknown';
      if (!deployedAt) return v;
      const d = new Date(deployedAt);
      if (isNaN(d.getTime())) return v;
      return `${v} · ${d.toLocaleDateString()}`;
    },

    formatDate(date) {
      if (window.VueUse !== undefined) {
        return VueUse.useTimeAgo(new Date(date));
      }
      return date.slice(0, 16).replace('T', ' ');
    },

    async onUpdate() {
      await this.pwa.applyUpdate();
    },
    async onInstall() {
      await this.pwa.install();
    },
    onReload() {
      if (typeof window !== 'undefined') window.location.reload();
    },
  },

  setup() {
    const user = useUser();
    const db = useDb();
    const networkStatus = useNetworkStatus();

    let pwa;
    try {
      pwa = usePwa();
    } catch (_) {
      pwa = PWA_FALLBACK;
    }

    return { user, db, networkStatus, pwa };
  },

  template: `
    <div class="dropdown">

      <!-- Trigger -->
      <button
        type="button"
        class="btn btn-secondary position-relative"
        data-bs-toggle="dropdown"
        aria-expanded="false"
      >
        <i :class="appIcon"></i>
        <span v-if="appName" class="ms-2">{{ appName }}</span>
        <span
          v-if="attentionLevel"
          class="position-absolute top-0 start-100 translate-middle rounded-circle border border-light"
          :class="attentionLevel === 'error' ? 'bg-danger' : 'bg-primary'"
          style="width:10px;height:10px;"
        ></span>
      </button>

      <!-- Dropdown -->
      <ul class="dropdown-menu dropdown-menu-end p-0" style="min-width:420px;" @click.stop>

        <!-- Sync error banner -->
        <li v-if="showSync && hasSyncErrors" class="px-3 py-2 bg-danger bg-opacity-10">
          <div class="d-flex align-items-center gap-2">
            <i class="bi bi-exclamation-circle text-danger flex-shrink-0"></i>
            <small class="text-danger">Sync error — see details in the sync section below</small>
          </div>
        </li>

        <!-- Update available banner -->
        <li v-if="showPwa && pwa.updateAvailable" class="px-3 py-2 bg-primary bg-opacity-10">
          <div class="d-flex align-items-center gap-2">
            <i class="bi bi-arrow-clockwise text-primary flex-shrink-0"></i>
            <small class="flex-grow-1">Update available — {{ formattedAvailableVersion }}</small>
            <button
              type="button"
              class="btn btn-primary btn-sm py-0"
              :disabled="pwa.installingUpdate"
              @click="onUpdate"
            >
              <template v-if="pwa.installingUpdate">
                <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>Preparing…
              </template>
              <template v-else>
                Update now
              </template>
            </button>
          </div>
        </li>

        <!-- ── User section ──────────────────────────────── -->
        <li v-if="showUser && hasBanners"><hr class="dropdown-divider m-0"></li>
        <template v-if="showUser">
          <li class="p-3">
            <UserData />
          </li>
          <li class="px-3 pb-3 d-flex justify-content-end align-items-center gap-2">
            <button
              class="btn btn-outline-secondary"
              data-bs-toggle="modal"
              data-bs-target="#appButtonUserPanelModal"
            >
              <i class="bi bi-three-dots-vertical"></i>
            </button>
            <button
              class="btn btn-outline-secondary"
              data-bs-toggle="modal"
              data-bs-target="#appButtonSettingsModal"
            >
              <i class="bi bi-gear me-1"></i>Settings
            </button>
            <UserAuthButtons />
          </li>
        </template>

        <!-- ── Sync section ──────────────────────────────── -->
        <li v-if="showSync && (hasBanners || showUser)"><hr class="dropdown-divider m-0"></li>
        <template v-if="showSync">
          <li v-if="!networkStatus.isOnline" class="px-3 pt-2">
            <div class="d-flex align-items-center gap-2 text-warning">
              <i class="bi bi-wifi-off flex-shrink-0"></i>
              <small>Offline — changes will sync when reconnected</small>
            </div>
          </li>
          <li class="px-3 pt-3 pb-1">
            <div
              v-for="(col, name) in collections"
              :key="name"
              class="d-flex justify-content-between align-items-center mb-2"
            >
              <div>
                <span class="fw-semibold small">{{ name }}</span>
                <span class="badge bg-secondary ms-1" style="font-size:0.65rem;">
                  {{ col.items?.length || 0 }}
                </span>
                <span v-if="col.syncErrors.length" class="badge bg-danger ms-1" style="font-size:0.65rem;">
                  {{ col.syncErrors.length }} error{{ col.syncErrors.length > 1 ? 's' : '' }}
                </span>
                <br>
                <small class="text-info" v-if="col.isMigrating">
                  <i class="bi bi-arrow-repeat spin"></i> Migrating data<span v-if="col.migrationProgress?.total"> ({{ col.migrationProgress.handled }}/{{ col.migrationProgress.total }})</span>
                </small>
                <small class="text-muted" v-else-if="col.isInSync"><i class="bi bi-check-circle text-success"></i> Synchronized {{ formatDate(col.lastSuccessfulSync) }}</small>
                <small class="text-muted" v-else-if="col.isLocalOnly"><i class="bi bi-circle text-muted"></i> Local-only data</small>
                <small class="text-muted" v-else-if="!col.lastSuccessfulSync"><i class="bi bi-hourglass-split"></i> Waiting for initial sync</small>
                <small class="text-muted" v-else><i class="bi bi-x-circle text-warning"></i> Out of sync, last sync {{ formatDate(col.lastSuccessfulSync) }}</small>
                <small class="text-danger ms-1" v-if="col.lastSyncErrorMessage && !col.isMigrating && (col.syncErrors.length || (!col.isLocalOnly && !col.isInSync && col.lastSuccessfulSync))">{{ col.lastSyncErrorMessage }}</small>
              </div>
              <button
                v-if="!col.isLocalOnly"
                class="btn btn-sm btn-outline-secondary"
                @click="col.resync()"
                :disabled="!col.getReplicationState || col.isMigrating"
              >
                <i class="bi bi-arrow-repeat"></i>
              </button>
            </div>
          </li>
          <li class="px-3 pb-3 d-flex justify-content-end gap-2">
            <button class="btn btn-sm btn-outline-secondary" @click="db.resyncAll">
              <i class="bi bi-arrow-repeat me-1"></i>Sync all
            </button>
            <button v-if="showHardResetButton" class="btn btn-sm btn-outline-danger" @click="db.hardReset">
              <i class="bi bi-trash me-1"></i>Hard reset
            </button>
            <button
              v-if="showDataButton"
              class="btn btn-sm btn-outline-secondary"
              data-bs-toggle="modal"
              data-bs-target="#appButtonDataPortabilityModal"
            >
              <i class="bi bi-database-fill-gear me-1"></i>Data
            </button>
          </li>
        </template>

        <!-- ── App / PWA section ─────────────────────────── -->
        <li v-if="showPwa && (hasBanners || showUser || showSync)"><hr class="dropdown-divider m-0"></li>
        <template v-if="showPwa">
          <li class="px-3 py-2">
            <small class="text-muted">{{ formattedCurrentVersion }}</small>

            <template v-if="primaryAction === 'install'">
              <div class="small text-muted mt-1 mb-2">{{ actionMessage }}</div>
              <button type="button" class="btn btn-primary btn-sm w-100" @click="onInstall">
                <i class="bi bi-download me-1"></i>Install
              </button>
            </template>
            <template v-else-if="primaryAction === 'reload'">
              <div class="small text-muted mt-1 mb-2">{{ actionMessage }}</div>
              <button type="button" class="btn btn-primary btn-sm w-100" @click="onReload">
                <i class="bi bi-arrow-repeat me-1"></i>Reload
              </button>
            </template>
            <template v-else-if="primaryAction === 'ios-help'">
              <div class="small mt-1 mb-1">{{ actionMessage }}</div>
              <ol class="small mb-0 ps-3">
                <li>Tap the <i class="bi bi-box-arrow-up"></i> Share button.</li>
                <li>Choose <strong>Add to Home Screen</strong>.</li>
              </ol>
            </template>
            <template v-else-if="primaryAction === 'offline'">
              <div class="small text-muted mt-1">{{ actionMessage }}</div>
            </template>
            <!-- 'online' and 'none': show Chrome/Edge hint if browser can't install -->
            <template v-else-if="showInstallHint">
              <div class="small text-muted mt-1">
                <i class="bi bi-download me-1"></i>Install for offline use with Chrome or Edge
              </div>
            </template>
          </li>
        </template>

      </ul>

      <!-- ── Modals ─────────────────────────────────────── -->

      <div v-if="showUser" class="modal fade" id="appButtonUserPanelModal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-lg">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">User Management</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="modal-body">
              <UserPanel />
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="showUser" class="modal fade" id="appButtonSettingsModal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-lg">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Settings</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="modal-body d-flex flex-column gap-2">
              <ul class="nav nav-tabs">
                <li class="nav-item" v-for="config in configs" :key="config.name">
                  <button
                    class="nav-link"
                    :class="{ active: activeConfig === config.name }"
                    @click="activeConfig = config.name"
                  >
                    {{ config.schema.title ?? config.name }}
                  </button>
                </li>
              </ul>
              <template v-for="config in configs" :key="config.name">
                <ConfigEditor
                  v-if="config.name === activeConfig"
                  :configName="config.name"
                  :defaults="{ displayMode: 'grid-horizontal' }"
                  :mode="config.replicationHandler ? 'form' : 'edit'"
                />
              </template>
            </div>
          </div>
        </div>
      </div>

      <div v-if="showSync && showDataButton" class="modal fade" id="appButtonDataPortabilityModal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-xl">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title"><i class="bi-database-fill-gear me-2"></i>Data Portability</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="modal-body">
              <DataPortabilityPanel />
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
            </div>
          </div>
        </div>
      </div>

    </div>
  `,
};
